import type { InputSetCode, UploadConfig } from '../types';

// Source: PRODUCT_DATA_MODEL.md §10 INPUT_CONFIGS
export const INPUT_CONFIGS: Record<InputSetCode, UploadConfig> = {
  'default': {
    showSubjectCount: true,
    showHumanUpload: true,
    showPetUpload: false,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 4,
    portraitMode: '',
  },
  'pet': {
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: true,
    showHumanName: false,
    showPetName: true,
    petNameRequired: true,
    showPetYears: true,
    showPetQuote: true,
    maxSubjects: 1,
    portraitMode: '',
  },
  'pet-angle': {
    // Only "Pet's name" (optional, ≤25) — no years/quote.
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: true,
    showHumanName: false,
    showPetName: true,
    petNameRequired: false,
    petNameMaxLen: 25,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 1,
    portraitMode: '',
  },
  'humanpet': {
    // "Your name" + "Pet's name" — both optional.
    showSubjectCount: false,
    showHumanUpload: true,
    showPetUpload: true,
    showHumanName: true,
    showPetName: true,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 2,
    portraitMode: 'humanpet',
  },
  'humanpet-angle': {
    // Same as humanpet — both names optional.
    showSubjectCount: false,
    showHumanUpload: true,
    showPetUpload: true,
    showHumanName: true,
    showPetName: true,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 2,
    portraitMode: 'humanpet_angle',
  },
  'humanpet-caption': {
    // Two uploads (Your photo / Pet photo) — no per-photo names. A single
    // "Family name / message" caption (≤25) is burned onto the portrait.
    showSubjectCount: false,
    showHumanUpload: true,
    showPetUpload: true,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 2,
    portraitMode: 'humanpet-caption',
    showCaption: true,
    captionLabel: 'Family name / message',
    captionMaxLen: 25,
  },
  'smallfamilypet-caption': {
    // Two gallery dropzones (Adult ≤2, Kids & Pets ≤3) + single caption (≤25).
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: false,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 4,
    portraitMode: 'smallfamilypet-caption',
    showCaption: true,
    captionLabel: 'Family name / message',
    captionMaxLen: 25,
    uploadGroups: [
      { key: 'adult', label: 'People photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add people photo(s)', emptyHint: 'Upload up to 2 people photos' },
      { key: 'kidspet', label: 'Pets photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add pets photo(s)', emptyHint: 'Upload up to 2 pets photos' },
    ],
  },
  'familypet-caption': {
    // The Forever Bond. Same two gallery dropzones as smallfamilypet-caption
    // (People ≤2, Pets ≤3) + single caption (≤25), plus a "Clothing Preference"
    // card picker rendered ABOVE the caption. The chosen id is sent as `clothing`
    // so the bridge can append a clothing instruction to the prompt.
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: false,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 4,
    portraitMode: 'familypet-caption',
    showCaption: true,
    captionLabel: 'Family name / message',
    captionMaxLen: 25,
    uploadGroups: [
      { key: 'adult', label: 'People photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add people photo(s)', emptyHint: 'Upload up to 2 people photos' },
      { key: 'kidspet', label: 'Pets photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add pets photo(s)', emptyHint: 'Upload up to 2 pets photos' },
    ],
    showClothing: true,
    clothingLabel: 'Clothing Preference',
    clothingDefaultId: 'artist_choice',
    clothingOptions: [
      {
        id: 'keep_original',
        icon: '📷',
        title: 'Keep Original',
        sub: 'Keep your real outfits exactly as they appear in your photos.',
      },
      {
        id: 'artist_choice',
        icon: '✨',
        title: "Artist's Choice",
        sub: 'Let the artist style elegant, coordinated attire that best matches the scene.',
        recommended: true,
      },
    ],
  },
  'familypet-caption-45': {
    // SP7/SP8 (Legacy Collections — Greige / Teal & Beige). Identical shape
    // to `familypet-caption`, kept as a separate input_set_code/portraitMode
    // purely so its seedream-4-5 routing (SeedreamClient.php) can never
    // affect the live `familypet-caption` product (Forever Bond).
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: false,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 4,
    portraitMode: 'familypet-caption-45',
    showCaption: true,
    captionLabel: 'Family name / message',
    captionMaxLen: 25,
    dynamicPersonPetGroups: true,
    uploadGroups: [
      { key: 'adult', label: 'People photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add people photo(s)', emptyHint: 'Upload up to 2 people photos' },
      { key: 'kidspet', label: 'Pets photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add pets photo(s)', emptyHint: 'Upload up to 2 pets photos' },
    ],
    showClothing: true,
    clothingLabel: 'Clothing Preference',
    clothingDefaultId: 'artist_choice',
    clothingOptions: [
      {
        id: 'keep_original',
        icon: '📷',
        title: 'Keep Original',
        sub: 'Keep your real outfits exactly as they appear in your photos.',
      },
      {
        id: 'artist_choice',
        icon: '✨',
        title: "Artist's Choice",
        sub: 'Let the artist style elegant, coordinated attire that best matches the scene.',
        recommended: true,
      },
    ],
  },
  'familypet-caption-50': {
    // SP9/SP10 (Heirloom Collections — Chocolate & Taupe / Greige & Sage-stone).
    // Identical shape to `familypet-caption`, and wants the same seedream-5-0
    // model — kept as its own mode string only to stay isolated from the
    // live `familypet-caption` product (Forever Bond).
    showSubjectCount: false,
    showHumanUpload: false,
    showPetUpload: false,
    showHumanName: false,
    showPetName: false,
    petNameRequired: false,
    showPetYears: false,
    showPetQuote: false,
    maxSubjects: 4,
    portraitMode: 'familypet-caption-50',
    showCaption: true,
    captionLabel: 'Family name / message',
    captionMaxLen: 25,
    dynamicPersonPetGroups: true,
    uploadGroups: [
      { key: 'adult', label: 'People photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add people photo(s)', emptyHint: 'Upload up to 2 people photos' },
      { key: 'kidspet', label: 'Pets photos', subLabel: 'Add up to 2', max: 2, emptyTitle: 'Add pets photo(s)', emptyHint: 'Upload up to 2 pets photos' },
    ],
    showClothing: true,
    clothingLabel: 'Clothing Preference',
    clothingDefaultId: 'artist_choice',
    clothingOptions: [
      {
        id: 'keep_original',
        icon: '📷',
        title: 'Keep Original',
        sub: 'Keep your real outfits exactly as they appear in your photos.',
      },
      {
        id: 'artist_choice',
        icon: '✨',
        title: "Artist's Choice",
        sub: 'Let the artist style elegant, coordinated attire that best matches the scene.',
        recommended: true,
      },
    ],
  },
};

export function getInputConfig(code: string | null | undefined): UploadConfig {
  const key = (code as InputSetCode) || 'default';
  return INPUT_CONFIGS[key] ?? INPUT_CONFIGS['default'];
}
